import React, { useState, useEffect, useRef } from 'react';
import { apiRequest } from '../utils/api';

const CustomersList = ({ customers: initialCustomers, onCustomerSelect, onRefresh }) => {
  const [customers, setCustomers] = useState(initialCustomers || []);
  const [loading, setLoading] = useState(false);
  const [refreshingOpenAI, setRefreshingOpenAI] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc'
  const [filterActive, setFilterActive] = useState('all'); // 'all' | 'active' | 'inactive'
  const [filterAffiliate, setFilterAffiliate] = useState('');
  const [affiliatesList, setAffiliatesList] = useState([]);
  const affiliateFilterInitialized = useRef(false);

  useEffect(() => {
    if (!initialCustomers || initialCustomers.length === 0) {
      fetchCustomers();
    } else {
      setCustomers(initialCustomers);
    }
  }, [initialCustomers]);

  useEffect(() => {
    const fetchAffiliates = async () => {
      try {
        const data = await apiRequest('/admin/affiliates?limit=500');
        if (data && data.success) setAffiliatesList(data.data || []);
      } catch (e) {
        console.error('Error fetching affiliates:', e);
      }
    };
    fetchAffiliates();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      if (filterAffiliate) params.set('affiliate', filterAffiliate);
      const url = params.toString() ? `/admin/customers?${params.toString()}` : '/admin/customers';
      const data = await apiRequest(url);
      if (data && data.success) {
        setCustomers(data.data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!affiliateFilterInitialized.current) {
      affiliateFilterInitialized.current = true;
      return;
    }
    fetchCustomers();
  }, [filterAffiliate]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    // Debounce search
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      fetchCustomers();
    }, 500);
  };

  const handleRefreshOpenAICosts = async () => {
    setRefreshingOpenAI(true);
    setMessage({ type: '', text: '' });
    try {
      const data = await apiRequest('/admin/openai/process-all', {
        method: 'POST',
        body: JSON.stringify({ daysBack: 180 }),
      });
      if (data.success) {
        setMessage({ 
          type: 'success', 
          text: data.message || `تم معالجة ${data.data?.customersProcessed || 0} عميل. تمت معالجة ${data.data?.totalProcessed || 0} تكلفة بقيمة ${data.data?.totalCost || 0}` 
        });
        // Refresh customers list to show updated balances
        fetchCustomers();
        if (onRefresh) onRefresh();
      } else {
        setMessage({ type: 'error', text: data.message || 'حدث خطأ أثناء معالجة تكاليف OpenAI' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'حدث خطأ في الاتصال بالخادم' });
      console.error('Error refreshing OpenAI costs:', error);
    } finally {
      setRefreshingOpenAI(false);
    }
  };

  if (loading && customers.length === 0) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        جاري التحميل...
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">قائمة العملاء</h2>
          <div className="flex gap-2">
            <button 
              className="btn btn-info btn-sm" 
              onClick={handleRefreshOpenAICosts}
              disabled={refreshingOpenAI}
            >
              {refreshingOpenAI ? 'جاري المعالجة...' : 'تحديث رصيد OpenAI للجميع'}
            </button>
            <button className="btn btn-primary btn-sm" onClick={fetchCustomers}>
              🔄 تحديث
            </button>
            <select className="form-control" value={sortField} onChange={(e) => setSortField(e.target.value)} style={{ width: '180px' }}>
              <option value="name">الاسم</option>
              <option value="email">البريد الإلكتروني</option>
              <option value="aiCredits">رصيد AI</option>
              <option value="smsCredits">رصيد SMS</option>
              <option value="openaiBalance">رصيد OpenAI</option>
            </select>
            <select className="form-control" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} style={{ width: '120px' }}>
              <option value="asc">تصاعدي</option>
              <option value="desc">تنازلي</option>
            </select>
            <select className="form-control" value={filterActive} onChange={(e) => setFilterActive(e.target.value)} style={{ width: '160px' }}>
              <option value="all">الكل</option>
              <option value="active">نشط</option>
              <option value="inactive">غير نشط</option>
            </select>
            <select
              className="form-control"
              value={filterAffiliate}
              onChange={(e) => setFilterAffiliate(e.target.value)}
              style={{ width: '200px' }}
            >
              <option value="">كل الشركاء</option>
              {affiliatesList.map((a) => (
                <option key={a._id} value={a._id}>{a.name}{a.code ? ` (${a.code})` : ''}</option>
              ))}
            </select>
          </div>
        </div>

        {message.text && (
          <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
            <span>{message.type === 'success' ? '✅' : '❌'}</span>
            <span>{message.text}</span>
          </div>
        )}

        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
          <input
            type="text"
            className="form-control"
            placeholder="بحث عن عميل بالاسم أو البريد..."
            value={searchTerm}
            onChange={handleSearch}
            style={{ maxWidth: '400px' }}
          />
        </div>

        {customers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👤</div>
            <div className="empty-state-title">لا يوجد عملاء</div>
            <div className="empty-state-text">ابدأ بإضافة عميل جديد</div>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>الاسم</th>
                  <th>البريد الإلكتروني</th>
                  <th>رصيد AI</th>
                  <th>رصيد SMS</th>
                  <th>رصيد OpenAI</th>
                  <th>الحالة</th>
                  <th>الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {([...customers]
                  .filter((c) => {
                    if (filterActive === 'active' && !c.isActive) return false;
                    if (filterActive === 'inactive' && c.isActive) return false;
                    const s = searchTerm.trim().toLowerCase();
                    if (!s) return true;
                    const fields = [
                      c.name || '',
                      c.email || '',
                    ].map((v) => v.toLowerCase());
                    return fields.some((v) => v.includes(s));
                  })
                  .sort((a, b) => {
                    const dir = sortOrder === 'asc' ? 1 : -1;
                    const getVal = (obj) => {
                      if (sortField === 'aiCredits') return obj.balances?.aiCredits || 0;
                      if (sortField === 'smsCredits') return obj.balances?.smsCredits || 0;
                      if (sortField === 'openaiBalance') return obj.balances?.openaiBalance || 0;
                      return (obj[sortField] || '').toString().toLowerCase();
                    };
                    const va = getVal(a);
                    const vb = getVal(b);
                    if (va < vb) return -1 * dir;
                    if (va > vb) return 1 * dir;
                    return 0;
                  }))
                  .map((customer) => (
                  <tr key={customer._id}>
                    <td><strong>{customer.name}</strong></td>
                    <td>{customer.email}</td>
                    <td>{customer.balances?.aiCredits || 0}</td>
                    <td>{customer.balances?.smsCredits || 0}</td>
                    <td>{customer.balances?.openaiBalance || 0}</td>
                    <td>
                      <span className={`badge ${customer.isActive ? 'badge-success' : 'badge-danger'}`}>
                        {customer.isActive ? 'نشط' : 'غير نشط'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => onCustomerSelect(customer)}
                      >
                        عرض التفاصيل
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomersList;
